import { useAppStore } from "@/store/app-store";
import { useDomainStore } from "@/store/domain-store";
import { useUserStore } from "@/store/user-store";
import {
  type AuthorityGroupPolicy,
  CreateTopicAuthorityGroupPolicyDefault,
  UpdateTopicAuthorityGroupPolicyDefault,
} from "@/support-acl-types";
import type Acl from "./acl";
import type { AclCreateType } from "./acl";
import { Comment } from "./comment";
import { Message } from "./message";
import { Topic } from "./topic";
import { User } from "./user";

enum Type {
  TOPIC,
  MESSAGE,
  COMMENT,
  REACTIONS,
}

type TopicType =
  | Topic
  | {
      domainId: string;
      acl?: Acl;
    };

/**
 * 「フルストア参照モード」を表すマーカー。
 * 旧実装で Vuex ストアを渡していた箇所には、本マーカー
 * (`AclManager.store`) を渡す。内部では Pinia ストア(useXxxStore)を参照する。
 * `User` を渡した場合は従来どおり「me のみ参照モード」となる。
 */
const ACL_FULL_STORE = Object.freeze({ __aclFullStore: true as const });
type AclFullStore = typeof ACL_FULL_STORE;
type AclStoreOrMe = AclFullStore | User;

export default class AclManager {
  /** フルストア参照モードを表すマーカー（旧 Vuex ストアの代替） */
  public static readonly store: AclFullStore = ACL_FULL_STORE;

  /** read権限が許可されてるかどうか */
  public static readTopic(user: User, target: TopicType): boolean {
    if (!target) return false; // 削除直後など対象が undefined のケースを許容
    const topic = target;
    if (target.acl?.readTopicByGuest()) {
      // ゲスト許可 → 全員許可 (2022/03)
      return true;
    } else {
      // ゲスト不許可なので判定する
      const isGuest = user.isGuestRole(topic.domainId);
      if (isGuest) {
        return false;
      } else {
        return true;
      }
    }
  }

  public static readMessage(
    $storeOrMe: AclStoreOrMe,
    topic: TopicType,
  ): boolean {
    return AclManager.read($storeOrMe, topic);
  }
  public static readComment(
    $storeOrMe: AclStoreOrMe,
    topic: TopicType,
  ): boolean {
    return AclManager.read($storeOrMe, topic);
  }
  private static read($storeOrMe: AclStoreOrMe, target: TopicType): boolean {
    if (!$storeOrMe) return false;
    if (!target) return false; // 削除直後など対象が undefined のケースを許容
    const _me = $storeOrMe instanceof User ? $storeOrMe : useUserStore().me;
    if (AclManager.readTopic(_me, target) === false) return false;
    return true;
  }

  /* ログインユーザーが作成権があるかどうかACL観点で判定する */
  public static createTopic(
    $storeOrMe: AclStoreOrMe,
    domainId: string,
  ): boolean {
    return AclManager.create($storeOrMe, domainId, Type.TOPIC);
  }
  public static createMessage(
    $storeOrMe: AclStoreOrMe,
    domainId: string,
  ): boolean {
    return AclManager.create($storeOrMe, domainId, Type.MESSAGE);
  }
  public static createComment(
    $storeOrMe: AclStoreOrMe,
    domainId: string,
  ): boolean {
    return AclManager.create($storeOrMe, domainId, Type.COMMENT);
  }
  private static create(
    $storeOrMe: AclStoreOrMe,
    domainId: string,
    type: Type,
  ): boolean {
    if (type === Type.COMMENT) return true; // コメントはゲスト許可

    // あとはゲスト不可
    if (!$storeOrMe) return false;
    const _me = $storeOrMe instanceof User ? $storeOrMe : useUserStore().me;
    if (!_me) return false;
    if (
      type === Type.TOPIC &&
      !($storeOrMe instanceof User) &&
      !AclManager.createTopicAclByDomain(domainId)
    ) {
      return false;
    }
    return !_me.isGuestRole(domainId);
  }

  /**
   * ログインユーザーが更新権があるかどうかACL観点で判定する
   * @param $storeOrMe AclManager.store もしくは store.getters.me のオブジェクトを指定する
   */
  public static updateTopic(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    return AclManager.update($storeOrMe, target);
  }
  public static updateMessage(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    return AclManager.update($storeOrMe, target);
  }
  public static updateComment(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    return AclManager.update($storeOrMe, target);
  }
  public static update(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    return AclManager._change($storeOrMe, target);
  }

  /* ログインユーザーが削除権があるかどうかACL観点で判定する */
  public static deleteTopic(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    return AclManager.delete($storeOrMe, target);
  }
  public static deleteMessage(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    return AclManager.delete($storeOrMe, target);
  }
  public static deleteComment(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    return AclManager.delete($storeOrMe, target);
  }
  public static delete(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    return AclManager._change($storeOrMe, target);
  }

  private static _change(
    $storeOrMe: AclStoreOrMe,
    target: Topic | Message | Comment,
  ): boolean {
    if (!$storeOrMe) return false;
    if (!target) return false; // 削除直後など対象が undefined のケースを許容
    const _me: User =
      $storeOrMe instanceof User ? $storeOrMe : useUserStore().me;
    if (!_me) return false;

    const domainId = target.domainId;
    const update = target.acl?.base?.update;
    if (update === undefined) {
      // 旧データ
      if (target instanceof Comment) {
        if (_me.id !== target.owner) return false;
        else return true; // コメントは更新OK
      } else if (_me.isGuestRole(target.domainId)) {
        return false;
      } else if (target instanceof Message) {
        if (_me.id !== target.owner) return false;
        else return true;
      } else {
        if (
          !($storeOrMe instanceof User) &&
          !AclManager.updateTopicAclByDomain(domainId, target as Topic)
        )
          return false;
        return true;
      }
    } else {
      if (update) {
        if (target instanceof Comment) {
          if (_me.id !== target.owner) return false;
          if ($storeOrMe instanceof User) {
            return true;
          } else {
            const result = AclManager.getCreateCommentAcl(
              AclManager.store,
              target.domainId,
              target.topicId,
              target.domainId,
            );
            return result !== "deny";
          }
        } else if (_me.isGuestRole(target.domainId)) {
          return false;
        } else if (target instanceof Message) {
          if (_me.id !== target.owner) return false;
          if ($storeOrMe instanceof User) {
            return true;
          } else {
            const result = AclManager.getCreateMessageAcl(
              AclManager.store,
              target.domainId,
              target.topicId,
            );
            return result !== "deny";
          }
        } else if (target instanceof Topic) {
          if (
            !($storeOrMe instanceof User) &&
            !AclManager.updateTopicAclByDomain(domainId, target)
          )
            return false;
          return true;
        } else {
          return false;
        }
      } else {
        // update:trueと同じ処理
        if (target instanceof Comment) {
          if (_me.id !== target.owner) return false;
          if ($storeOrMe instanceof User) {
            return true;
          } else {
            const result = AclManager.getCreateCommentAcl(
              AclManager.store,
              target.domainId,
              target.topicId,
              target.domainId,
            );
            return result !== "deny";
          }
        } else if (_me.isGuestRole(target.domainId)) {
          return false;
        } else if (target instanceof Message) {
          if (_me.id !== target.owner) return false;
          if ($storeOrMe instanceof User) {
            return true;
          } else {
            const result = AclManager.getCreateMessageAcl(
              AclManager.store,
              target.domainId,
              target.topicId,
            );
            return result !== "deny";
          }
        } else if (target instanceof Topic) {
          if (
            !($storeOrMe instanceof User) &&
            !AclManager.updateTopicAclByDomain(domainId, target)
          )
            return false;
          return true;
        } else {
          return false;
        }
      }
    }
  }

  /** ログインユーザーがゲストかどうか判定する */
  public static isGuest($store: AclStoreOrMe, domainId: string): boolean {
    if (!$store) return true;
    const me = useUserStore().me as User | undefined;
    if (!me) return true;
    return me.isGuestRole(domainId);
  }

  // Create〜 の状態を取得する

  /**
   * createMesssageAcl を解決する（Topic から解決する）
   */
  public static getCreateMessageAcl(
    $store: AclStoreOrMe,
    domainId: string,
    topicId: string,
  ): AclCreateType {
    if (!$store) return "allow"; // storybook用

    const me = useAppStore().me;
    if (!me) return "deny";
    const topic = useAppStore().getTopic(domainId, topicId) as
      | Topic
      | undefined;
    if (!topic) return "deny";
    const acl = topic.acl;
    const owner = topic.owner;
    return AclManager.createXAcl(me, domainId, { owner, acl }, Type.MESSAGE);
  }

  /**
   * createCommentAcl を解決する
   */
  public static getCreateCommentAcl(
    $store: AclStoreOrMe,
    domainId: string,
    topicId: string,
    messageId?: string,
  ): AclCreateType {
    if (!$store) return "allow"; // storybook用

    const me = useAppStore().me;
    if (!me) return "deny";

    const topic = useAppStore().getTopic(domainId, topicId) as
      | Topic
      | undefined;
    if (!topic) return "deny";
    const topicAcl = topic.acl;
    const topicOwner = topic.owner;
    const topicResult = AclManager.createXAcl(
      me,
      domainId,
      { owner: topicOwner, acl: topicAcl },
      Type.COMMENT,
    );
    if (!messageId) {
      // 話題 のみでACL
      switch (topicResult) {
        case "allow":
        case "inherit": {
          return "allow";
        }
        case "deny":
          return "deny";
        default:
          return "inherit";
      }
    } else {
      switch (topicResult) {
        case "allow":
        case "inherit": {
          break;
        }
        case "deny":
          return "deny";
        default:
          return "inherit";
      }
    }

    // 投稿のACLも考える
    const message = useAppStore().getMessage(domainId, topicId, messageId) as
      | Message
      | undefined;
    if (!message) return "deny";
    const messageAcl = message.acl;
    const messageOwner = message.owner;
    const messageResult = AclManager.createXAcl(
      me,
      domainId,
      { owner: messageOwner, acl: messageAcl },
      Type.COMMENT,
    );
    switch (messageResult) {
      case "allow":
      case "inherit": {
        return AclManager.getCreateCommentAcl(
          AclManager.store,
          domainId,
          topicId,
        );
      }
      case "deny":
        return "deny";
      default:
        return "inherit";
    }
  }

  /**
   * createReactionAcl を解決する
   */
  public static getCreateReactionAcl(
    $store: AclStoreOrMe,
    domainId: string,
    topicId: string,
    messageId?: string,
    commentId?: string,
  ): AclCreateType {
    if (!$store) return "allow"; // storybook用

    const me = useAppStore().me;
    if (!me) return "deny";

    // Topic or Message or Comment の取得
    let target: Topic | Message | Comment;
    let type: Type;
    if (commentId && messageId) {
      target = useAppStore().getComment(
        domainId,
        topicId,
        messageId,
        commentId,
      ) as Comment;
      type = Type.COMMENT;
    } else if (messageId) {
      target = useAppStore().getMessage(
        domainId,
        topicId,
        messageId,
      ) as Message;
      type = Type.MESSAGE;
    } else {
      target = useAppStore().getTopic(domainId, topicId) as Topic;
      type = Type.TOPIC;
    }
    if (!target) return "deny";

    // createReactionAcl の解決
    const acl = target.acl;
    const owner = target.owner;
    const result = AclManager.createXAcl(
      me,
      domainId,
      { owner, acl },
      Type.REACTIONS,
    );
    switch (result) {
      case "inherit":
      case "allow": {
        // Reaction の allow は全コンテナで and 判定する
        let containerResult: AclCreateType;
        if (type === Type.COMMENT) {
          containerResult = AclManager.getCreateReactionAcl(
            AclManager.store,
            domainId,
            topicId,
            messageId,
          );
        } else if (type === Type.MESSAGE) {
          containerResult = AclManager.getCreateReactionAcl(
            AclManager.store,
            domainId,
            topicId,
          );
        } else {
          containerResult = "allow";
        }
        return containerResult === "allow" ? "allow" : "deny";
      }
      case "deny":
        return "deny";
      default:
        return "deny";
    }
  }

  // ownerIgnore: true設定にすると owner 設定を無視する
  private static createXAcl(
    me: User,
    domainId: string,
    context: { owner: string; acl: Acl },
    type: Type,
    ownerIgnore: boolean = false,
  ): AclCreateType {
    if (!me || !context) return "deny"; // 不許可

    if (ownerIgnore === false) {
      if (me.id === context.owner) return "allow"; // 作成者は許可
    }

    const acl = context.acl;
    const guest = me.isGuestRole(domainId);
    if (guest) {
      switch (type) {
        case Type.TOPIC:
          return acl.base.createTopic || "allow";
        case Type.MESSAGE:
          return acl.base.createMessage || "allow";
        case Type.COMMENT:
          return acl.guest.create &&
            (acl.base.createComment || "allow") !== "deny"
            ? "allow"
            : "deny";
        case Type.REACTIONS:
          return acl.guest.create &&
            (acl.base.createReactions || "allow") !== "deny"
            ? "allow"
            : "deny";
        default:
          return "deny";
      }
    } else {
      switch (type) {
        case Type.TOPIC:
          return acl.base.createTopic || "allow";
        case Type.MESSAGE:
          return acl.base.createMessage || "allow";
        case Type.COMMENT:
          return acl.base.createComment || "allow";
        case Type.REACTIONS:
          return acl.base.createReactions || "allow";
        default:
          return "deny";
      }
    }
  }

  // 話題作成権限 組織設定 処理
  private static createTopicAclByDomain(domainId: string): boolean {
    const me = useUserStore().me as User;
    const createTopicAuthorityGroupPolicy = useDomainStore()
      .getCreateTopicAuthorityGroupPolicy as AuthorityGroupPolicy;

    // 管理者で話題作成権限がない
    const createTopicAuthorityGroupPolicyByAdmin =
      createTopicAuthorityGroupPolicy.admin ??
      CreateTopicAuthorityGroupPolicyDefault.admin;
    if (!createTopicAuthorityGroupPolicyByAdmin && me.isAdminRole(domainId)) {
      return false;
    }

    // ユーザで話題作成権限がない
    const createTopicAuthorityGroupPolicyByUser =
      createTopicAuthorityGroupPolicy.user ??
      CreateTopicAuthorityGroupPolicyDefault.user;
    if (!createTopicAuthorityGroupPolicyByUser && me.isUserRole(domainId)) {
      return false;
    }

    return true;
  }

  // 話題更新権限 組織設定 処理
  private static updateTopicAclByDomain(
    domainId: string,
    topic: Topic,
  ): boolean {
    const me = useUserStore().me as User;

    // 所有者は話題を全て更新できる
    if (me.isOwnerRole(domainId)) return true;

    const updateTopicAuthorityGroupPolicy = useDomainStore()
      .getUpdateTopicAuthorityGroupPolicy as AuthorityGroupPolicy;

    // 管理者で話題更新権限がない
    const updateTopicAuthorityGroupPolicyByAdmin =
      updateTopicAuthorityGroupPolicy.admin ??
      UpdateTopicAuthorityGroupPolicyDefault.admin;
    if (updateTopicAuthorityGroupPolicyByAdmin && me.isAdminRole(domainId)) {
      return true;
    }

    // 実行者と作成者が同一ではない
    if (me.id !== topic.owner) return false;
    else return true;
  }
}
